import axios from "axios";
import { datadogRum } from "@datadog/browser-rum";
import { drw_endpoint_url } from "./api";
import { FetchBenefitsOptions, FraudMonitorParams, FraudMonitorResponse, SubmitFeedbackSurveyParams } from "./types";
import { createFetchBenefitsBody, createFetchBenefitsUrl } from "./helpers/drwHelpers";
import { PredictiveETAAddressDetails, PredictiveETAParams, PredictiveETAResponse } from "../types/PredictiveETAAddress";

export async function fetchBenefits(options: FetchBenefitsOptions, appId: string, partnerCode: string) {
  const url = createFetchBenefitsUrl({ appId, partnerCode });
  const body = createFetchBenefitsBody(options);

  let retry = 0;
  const maxRetries = 3;

  while (retry < maxRetries) {
    try {
      let response = await axios.post(url, body);
      if (retry) {
        datadogRum.addError(`[fetchOmniBenefits] Fetch benefits failed and recovered`, {
          appId,
          partnerCode,
          retriedResponse: response,
          status: response?.data?.status,
        });
      }
      datadogRum.addAction("[fetchOmniBenefits] Fetch benefits success", {
        partnerCode,
        status: response?.data?.status,
      });
      return response;
    } catch (err) {
      if (err.response?.status === 500 && retry < 2) {
        retry += 1;
      } else {
        datadogRum.addError(
          new Error(`Fetch Benefits- Omni Search API call failed with status ${err.response?.status}`),
          { body, err, partnerCode }
        );
        return err.response;
      }
    }
  }
}

export async function fraudMonitor(fraudMonitorParams: FraudMonitorParams): Promise<FraudMonitorResponse> {
  try {
    const response = await axios.post(
      `${drw_endpoint_url}/api/omniapi/${fraudMonitorParams.appId}/partner/${fraudMonitorParams.denyRequest.partnerCode}/fraudMonitor`,
      fraudMonitorParams.denyRequest
    );
    return response?.data;
  } catch (error) {
    fraudMonitorParams.denyRequest.error = error?.response?.data?.message;
    return { success: false };
  }
}

export async function predictiveETADRW({
  currentLocation,
  isTow,
  appId,
  sessionId,
  partnerCode,
  callId,
}: PredictiveETAParams): Promise<PredictiveETAResponse> {
  const addressDetails: PredictiveETAAddressDetails = {
    latitude: currentLocation.latitude,
    longitude: currentLocation.longitude,
    zipCode: currentLocation.zipCode,
    stateCode: currentLocation.state,
    towInd: isTow ? "Y" : "N",
    sourceName: appId,
    sessionId: sessionId,
    partnerCode: partnerCode,
    callId,
  };
  return await axios
    .post(`${drw_endpoint_url}/api/google/predictiveETA`, addressDetails)
    .then((response) => {
      datadogRum.addAction("PredictiveETADRW endpoint hit: ", addressDetails);
      return response?.data;
    })
    .catch((error) => {
      addressDetails.error = error;
      datadogRum.addError("Call to PredictiveETADRW API resulted in an error: ", addressDetails);
    });
}

export async function rescueCancellation(appId, displayCode, callId, cancelRequest, partnerCode) {
  try {
    const response = await axios.post(
      `${drw_endpoint_url}/api/omniapi/${appId}/partner/${displayCode}/callid/${callId}/cancel`,
      cancelRequest
    );
    if (response?.data?.errorMessage) {
      datadogRum.addError("Rescue Cancellation Error - response", {
        displayCode,
        callId,
        response,
        cancelRequest,
      });
    } else {
      return response.data;
    }
  } catch (error) {
    datadogRum.addError("Rescue Cancellation - An error occurred", {
      partnerCode,
      callId,
      error,
      cancelRequest,
    });
  }
}

export function getPartnerDetailsApiDRW(route: string, displayCode: string) {
  return axios.get(`${drw_endpoint_url}/api/drw/${route}/partner/${displayCode}`);
}

export async function getVerifiedRescueTrackerStatusDRW(partnerCode: string, usageId: string, callId: string) {
  try {
    const response = await axios.get(
      `${drw_endpoint_url}/api/drw/partner/${partnerCode}/usageid/${usageId}/callid/${callId}/rescuestatus`
    );
    if (response?.data?.error) {
      datadogRum.addError("rescueStatusTrackerError-response", {
        partnerCode,
        usageId,
        callId,
        response,
      });
    } else {
      return response.data;
    }
  } catch (error) {
    datadogRum.addError(
      `Rescue Status View Error: ${
        error?.response?.data?.error?.errors?.filter(
          (_x, index) => index === 0
        )?.[0] ?? error?.message
      }`,
      { rescueStatusViewError: { partnerCode, usageId, error } }
    );
    // 400 = rescue verification failed (partnerCode/callId mismatch or invalid params); let callers handle it.
    if (error?.response?.status === 400) {
      throw error;
    }
  }
}

export const submitFeedbackSurvey = async ({
  feedbackRequest,
  partnerCode = "",
  callId = "",
}: SubmitFeedbackSurveyParams) => {
  if (!partnerCode || !callId) {
    datadogRum.addError("Feedback submission skipped - missing data", { partnerCode: partnerCode, callId: callId });
    return;
  }
  if (callId.includes("-")) {
    datadogRum.addError("Feedback submission skipped - callId is UUID", { partnerCode: partnerCode, callId: callId });
    return;
  }
  return axios.post(`${drw_endpoint_url}/api/omniapi/${partnerCode}/${callId}/feedback-survey`, feedbackRequest);
};
